import {
  EVENT_VISITS_PER_EVENT_DAILY_CAP,
  EVENT_VISITS_PROTOCOL_VERSION,
  decodeEventVisitContribution,
  romeDay,
} from '@buzzmap/shared';
import type { EventVisitContribution } from '@buzzmap/shared';
import { readReturnVisitPreference, isReturnVisitHostEligible } from './returnVisits';

export const EVENT_VISITS_STORAGE_KEY = 'bm.eventVisits.v1';
export const EVENT_VISITS_LOCK_NAME = 'buzzmap-event-visits';

const UUID_RE = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
const CRAWLER_OR_PREVIEW_UA_RE =
  /(?:bot|crawl|spider|slurp|preview|prerender|headless|facebookexternalhit|twitterbot|linkedinbot|slackbot|discordbot|whatsapp|telegrambot)/i;
const ENDPOINT = '/api/event-visits';

type StoredEventVisits = {
  day: string;
  ids: string[];
};

interface EventVisitStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface EventVisitLocks {
  request(
    name: string,
    options: { mode: 'exclusive' },
    callback: (lock: unknown) => Promise<void>
  ): Promise<void>;
}

interface NavigatorPrivacySignals {
  readonly globalPrivacyControl?: boolean;
}

function getStorage(): EventVisitStorage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function getLocks(): EventVisitLocks | null {
  try {
    if (typeof navigator === 'undefined' || !navigator.locks) return null;
    return navigator.locks as unknown as EventVisitLocks;
  } catch {
    return null;
  }
}

function getHostname(): string {
  try {
    return typeof window === 'undefined' ? '' : window.location.hostname;
  } catch {
    return '';
  }
}

function browserSignalsAllowCollection(): boolean {
  try {
    if (typeof navigator === 'undefined') return false;
    const privacy = navigator as Navigator & NavigatorPrivacySignals;
    if (privacy.globalPrivacyControl === true) return false;
    if (navigator.doNotTrack === '1') return false;
    if (navigator.webdriver) return false;
    if (!Array.isArray(navigator.languages) || navigator.languages.length === 0) return false;
    if (CRAWLER_OR_PREVIEW_UA_RE.test(navigator.userAgent ?? '')) return false;
    return true;
  } catch {
    return false;
  }
}

function readStoredVisits(storage: EventVisitStorage): unknown {
  try {
    return storage.getItem(EVENT_VISITS_STORAGE_KEY);
  } catch {
    return undefined;
  }
}

function decodeStoredVisits(raw: unknown, today: string): StoredEventVisits {
  if (typeof raw !== 'string') return { day: today, ids: [] };
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { day: today, ids: [] };
    }
    const stored = value as Record<string, unknown>;
    if (stored.day !== today || !Array.isArray(stored.ids)) return { day: today, ids: [] };
    const ids = [
      ...new Set(
        stored.ids
          .filter((id): id is string => typeof id === 'string' && UUID_RE.test(id))
          .map((id) => id.toLowerCase())
      ),
    ].slice(0, EVENT_VISITS_PER_EVENT_DAILY_CAP);
    return { day: today, ids };
  } catch {
    // Corrupt local data is treated as an empty day, not as a fatal error.
    return { day: today, ids: [] };
  }
}

function isStorageReadable(storage: EventVisitStorage): boolean {
  try {
    storage.getItem(EVENT_VISITS_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

function isEligibleForEventVisits(): boolean {
  try {
    if (import.meta.env.VITE_EVENT_VISITS_ENABLED !== 'true') return false;
    if (!isReturnVisitHostEligible(getHostname())) return false;
    if (!browserSignalsAllowCollection()) return false;
    const preference = readReturnVisitPreference();
    if (preference.persistence !== 'ok' || preference.optedOut) return false;
    const storage = getStorage();
    if (storage === null || !isStorageReadable(storage)) return false;
    return true;
  } catch {
    return false;
  }
}

async function postContribution(contribution: EventVisitContribution): Promise<number | null> {
  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      keepalive: true,
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contribution),
    });
    return response.status;
  } catch {
    return null;
  }
}

/** Creates the browser-side state and transport for the event-visit measure. */
function createEventVisitRecorder() {
  let disabledForPage = false;

  async function reserveAndPost(eventId: string): Promise<void> {
    if (disabledForPage || !isEligibleForEventVisits()) return;
    const storage = getStorage();
    if (storage === null) return;

    try {
      const day = romeDay(new Date());
      const current = decodeStoredVisits(readStoredVisits(storage), day);
      if (current.ids.includes(eventId)) return;
      if (current.ids.length >= EVENT_VISITS_PER_EVENT_DAILY_CAP) return;

      const next: StoredEventVisits = { day, ids: [...current.ids, eventId] };
      storage.setItem(EVENT_VISITS_STORAGE_KEY, JSON.stringify(next));

      const contribution = decodeEventVisitContribution({
        version: EVENT_VISITS_PROTOCOL_VERSION,
        eventId,
        day,
      });
      if (contribution === null) return;
      if ((await postContribution(contribution)) === 403) disabledForPage = true;
    } catch {
      // Storage, protocol, lock, and transport failures are silent by contract.
    }
  }

  async function record(eventId: string): Promise<void> {
    try {
      const normalizedId = typeof eventId === 'string' ? eventId.toLowerCase() : '';
      if (!UUID_RE.test(normalizedId) || disabledForPage || !isEligibleForEventVisits()) return;
      const locks = getLocks();
      if (locks === null) {
        await reserveAndPost(normalizedId);
        return;
      }
      await locks.request(EVENT_VISITS_LOCK_NAME, { mode: 'exclusive' }, async () => {
        await reserveAndPost(normalizedId);
      });
    } catch {
      // A lock implementation can reject; never retry or surface the failure.
    }
  }

  return { record };
}

const defaultRecorder = createEventVisitRecorder();

/** One privacy-gated contribution attempt. It never rejects or retries. */
export function recordEventVisit(eventId: string): Promise<void> {
  return defaultRecorder.record(eventId);
}

/** Whether the current page can start accumulating a visible event visit. */
export function canRecordEventVisit(): boolean {
  return isEligibleForEventVisits();
}
